/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef, useState} from 'react';

export const SUPPRESS_REFRESH_MS = 1000;

export interface WatchedJobDropsProps {
  isWatchingTab: boolean,
  watchedJobIds: number[],
  revision: number,
  onDrop: (jobIds: number[]) => void
}

export interface WatchedJobDropsResult {
  suppressRefresh: boolean
}

const removedIds = (before: number[], after: number[]): number[] => (
  after.every(id => before.includes(id)) ? before.filter(id => !after.includes(id)) : []
);

/**
 * Turns an unwatch made while the Watching tab is displayed into a row leaving
 * the grid, rather than into the grid reloading.
 *
 * The tab's filter is built from the watch list, so a bell click changes the
 * query - which raises `refresh`, which is `refreshRequired` on the grid, which
 * rebuilds the datasource: every block purged, the page reset to 1 and a
 * spinner over rows that never left. `suppressRefresh` is what keeps that
 * rebuild off the one change we can serve in place, and `onDrop` is what serves
 * it.
 *
 * Only a removal is reported. An addition, and any change made while another
 * tab is displayed, reaches the grid through the ordinary load instead.
 *
 * ```typescript
 *
 *   import useWatchedJobDrops from './useWatchedJobDrops';
 *
 *   const {suppressRefresh} = useWatchedJobDrops({
 *     isWatchingTab : activeTab === WATCHING,
 *     revision      : watchedJobsRevision,
 *     onDrop        : dropJobs,
 *     watchedJobIds
 *   });
 *
 *   <JobSearchEngineGrid refresh={refresh && !suppressRefresh} />
 *
 * ```
 *
 * @param   isWatchingTab Whether the Watching tab is the displayed one.
 * @param   watchedJobIds The current watch list.
 * @param   revision The watch list's version counter, which is what a hook can
 *          depend on - the id array is a module singleton's own.
 * @param   onDrop Invoked with the ids one toggle removed.
 * @returns `suppressRefresh` while the grid must not rebuild its datasource.
 */
export const useWatchedJobDrops = ({
  isWatchingTab,
  watchedJobIds,
  revision,
  onDrop
}: WatchedJobDropsProps): WatchedJobDropsResult => {
  const previous = useRef<number[] | null>(null);
  const dropped  = useRef(onDrop);

  const [suppressRefresh, setSuppressRefresh] = useState(false);

  useEffect(() => {
    dropped.current = onDrop;
  });

  const lower = useCallback((): void => {
    setSuppressRefresh(false);
  }, []);

  useEffect(() => {
    const before = previous.current;

    previous.current = watchedJobIds;
    if (before && isWatchingTab) {
      const ids = removedIds(before, watchedJobIds);

      if (ids.length) {
        setSuppressRefresh(true);
        dropped.current(ids);
      }
    }
  }, [revision, isWatchingTab, watchedJobIds]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (suppressRefresh) {
      timeout = setTimeout(lower, SUPPRESS_REFRESH_MS);
    }
    return function() {
      clearTimeout(timeout);
    };
  }, [suppressRefresh, lower]);

  return {suppressRefresh};
};

export default useWatchedJobDrops;
